import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, TextField } from '@mui/material';
import filterIcon from 'assets/images/icons/filter.svg';
import ITSVG from 'common/helpers/ITSVG';
import { SuccessFilter } from 'common/types/success-filter.model';
import { AuthType } from 'common/types/auth-type.model';

interface SignInAuditFilterDropdownProps {
    handleFilterChange: (successFilter: SuccessFilter, route: AuthType | null) => void;
}

export function SignInAuditFilterDropdown({
    handleFilterChange
}: SignInAuditFilterDropdownProps) {
    const { t } = useTranslation();
    const [localSuccessFilter, setLocalSuccessFilter] = useState<SuccessFilter>(SuccessFilter.No);
    const [localAuthType, setLocalAuthType] = useState<AuthType>('User');

    useEffect(() => {
        handleFilterChange(SuccessFilter.No, 'User');
    }, []);

    const handleSubmit = () => {
        handleFilterChange(localSuccessFilter, localAuthType);
    };

    const successFilterOptions = [
        { label: t('generalMessages.yes'), value: SuccessFilter.Yes },
        { label: t('generalMessages.no'), value: SuccessFilter.No }
    ];

    const userTypeFilterOptions = [
        { label: t('table.userAuth'), value: 'User' as AuthType },
        { label: t('table.agentAuth'), value: 'Agent' as AuthType }
    ];

    return (
        <>
            <button
                type='button'
                className='btn btn-sm btn-flex btn-light btn-active-primary fw-bolder'
                data-kt-menu-trigger='click'
                data-kt-menu-placement='bottom-end'
                data-kt-menu-flip='top-end'
            >
                <ITSVG path={filterIcon} className='svg-icon-5 svg-icon-gray-500 me-1' />
                {t('generalMessages.filter')}
            </button>

            <div className='menu menu-sub menu-sub-dropdown w-250px w-md-300px' data-kt-menu='true'>
                <div className='px-7 py-5'>
                    <div className='fs-5 text-dark fw-bolder'>{t('generalMessages.filterOptions')}</div>
                </div>

                <div className='separator border-gray-200'></div>

                <div className='px-7 py-5'>
                    <div className='mb-10'>
                        <label className='form-label fw-bold'>{t('table.success')}</label>
                        <Autocomplete
                            id='success-filter'
                            options={successFilterOptions}
                            value={successFilterOptions.find(e => e.value === localSuccessFilter)}
                            onChange={(e, value) => {
                                e?.stopPropagation();
                                setLocalSuccessFilter(value?.value || SuccessFilter.No);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={`${t('generalMessages.select')}`}
                                />
                            )}
                        />
                    </div>

                    <div className='mb-10'>
                        <label className='form-label fw-bold'>{t('table.authType')}</label>
                        <Autocomplete
                            id='auth-type-filter'
                            options={userTypeFilterOptions}
                            value={userTypeFilterOptions.find(e => e.value === localAuthType)}
                            onChange={(e, value) => {
                                e?.stopPropagation();
                                setLocalAuthType(value?.value as AuthType);
                            }}

                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={`${t('generalMessages.select')}`}
                                />
                            )}
                        />
                    </div>

                    <div className='d-flex justify-content-end gap-2'>
                        <button
                            type='reset'
                            className='btn btn-sm btn-light btn-active-light-primary'
                            onClick={() => {
                                document.querySelector<HTMLElement>('[data-kt-menu="true"]')?.click();
                            }}
                        >
                            {t('generalMessages.cancel')}
                        </button>

                        <button
                            type='button'
                            onClick={() => {
                                handleSubmit();
                                document.querySelector<HTMLElement>('[data-kt-menu="true"]')?.click();
                            }}
                            className='btn btn-sm btn-primary'
                        >
                            {t('generalMessages.apply')}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}